/**
 * Replace every `%if[ condition ] ... endif%` or `%if[ condition ] ... %endif%` block in the given text.
 *
 * Supported operators — evaluated strictly **left → right** (no precedence):
 *
 *   • `&&`   logical AND
 *
 *   • `||`   logical OR
 *
 *   • `!`    unary NOT (immediately before a tag, e.g. `!beta`)
 *
 * Examples
 *
 *   • `tag1&&!tag2`      → true if tag1 **is** selected **and** tag2 is **not**
 *
 *   • `!tag1||tag2`      → true if tag1 is not selected **or** tag2 is
 *
 * @param text String that may contain conditional blocks.
 * @param activeTags Tags currently activated in the Action Card.
 * @returns Text with all conditional sections resolved.
 * @story User Story 11433: Conditions for AI prompts in AC widget
 */
export default function processConditionalReplacements(text: string, activeTags: readonly string[]): string {
  if (!text.includes('%if[')) return text

  // Convert active tags to a Set for fast lookup
  const tagSet = new Set(activeTags.map(t => t.trim()))

  /** Evaluate one condition string such as "tag1&&!tag2||tag3" */
  function evalCondition(condition: string): boolean {
    // Split into alternating [operand, operator, operand, ...]
    const tokens = condition
      .trim()
      .split(/(&&|\|\|)/) // keep the operators
      .map(t => t.trim())
      .filter(Boolean) // remove empty tokens

    let result: boolean | undefined
    let currentOp: '&&' | '||' | null = null

    for (const token of tokens) {
      if (token === '&&' || token === '||') {
        currentOp = token
        continue
      }

      // Operand: may start with "!"
      const isNegated = token.startsWith('!')
      const tag = isNegated ? token.slice(1) : token
      const value = isNegated ? !tagSet.has(tag) : tagSet.has(tag)

      if (result === undefined) {
        result = value // first operand
      } else if (currentOp === '&&') {
        result = result && value
      } else if (currentOp === '||') {
        result = result || value
      } else {
        // Should never happen if the input is well-formed
        console.error(`Unexpected token "${token}"`)
        result = false // fallback to false
      }
    }

    return result ?? false // default to false if empty
  }

  // Regex: %if[condition] ... endif% (also accepts %endif%)
  const blockPattern = /%if\[(.*?)\]([\s\S]*?)%?endif%/g // non-greedy so it stops at first closing token

  // Replace every block with either its body (if true) or ''.
  return text.replace(blockPattern, (_, cond: string, body: string) => (evalCondition(cond) ? body : ''))
}
